import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { NoteView } from "@/components/LongText";
import { Phone, X, ArrowRight, Check, CalendarClock, MapPin } from "lucide-react";

const localISO = (dt) => { const t = new Date(dt); t.setMinutes(t.getMinutes() - t.getTimezoneOffset()); return t.toISOString().slice(0, 10); };

export default function ChiamateReminder() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [esitoFor, setEsitoFor] = useState(null);
  const [rdate, setRdate] = useState("");

  const load = () => api.get("/chiamate").then((r) => {
    const today = localISO(new Date());
    const due = (r.data || []).filter((c) => (c.chiamata_entro || "") > "" && c.chiamata_entro <= today);
    setItems(due);
    if (due.length) setShowModal(true);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCard = (c) => {
    const iid = c.immobile_id;
    if (c.tipo === "immobile" || iid) nav(`/immobili/${iid || c.id}`);
    else nav(`/notizie/${c.id}`);
  };
  const complete = async (c, esito) => {
    try { await api.post(`/chiamate/${c.tipo}/${c.id}/completa`, { esito }); toast.success("Chiamata segnata come completata"); setEsitoFor(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reschedule = async (c) => {
    if (!rdate) return toast.error("Seleziona la nuova data");
    try { await api.post(`/chiamate/${c.tipo}/${c.id}/rimanda`, { data: rdate }); toast.success("Chiamata rimandata"); setRescheduleId(null); setRdate(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const snoozeTomorrow = async (c) => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    try { await api.post(`/chiamate/${c.tipo}/${c.id}/rimanda`, { data: localISO(d) }); toast.success("Chiamata rimandata a domani"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!items.length) return null;

  return (
    <>
      <div data-testid="chiamate-banner" onClick={() => setShowModal(true)}
           className="card-immo p-4 mb-8 border-[#F43F5E]/30 bg-[#F43F5E]/[0.06] flex items-center gap-3 cursor-pointer hover:-translate-y-0.5 transition-transform">
        <div className="w-10 h-10 rounded-xl bg-[#F43F5E]/15 border border-[#F43F5E]/30 flex items-center justify-center shrink-0"><Phone className="text-[#F43F5E]" size={20} /></div>
        <div className="text-sm text-slate-700">
          <b className="text-[#111111]">{items.length}</b> {items.length === 1 ? "chiamata programmata per oggi" : "chiamate programmate per oggi"}. Tocca per gestirle.
        </div>
        <ArrowRight size={16} className="text-[#F43F5E] ml-auto" />
      </div>

      {showModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="chiamate-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative w-full max-w-lg max-h-[calc(100vh-2rem)] flex flex-col card-immo overflow-hidden">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[var(--border)] shrink-0">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><Phone size={18} className="text-[#F43F5E]" /> È programmata una chiamata</h3>
              <button onClick={() => setShowModal(false)} data-testid="chiamate-modal-close" className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="space-y-3 px-6 py-4 flex-1 overflow-y-auto overscroll-contain">
              {items.map((c) => (
                <div key={`${c.tipo}-${c.id}`} data-testid={`chiamata-item-${c.id}`} className="rounded-xl border border-[var(--border)] p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#F43F5E]/10 text-[#F43F5E] border border-[#F43F5E]/30">{c.tipo === "immobile" ? "Incarico" : "Notizia"}</span>
                    <CalendarClock size={13} className="text-[var(--muted)]" />
                    <span className="text-[11px] text-[var(--muted)]">Entro {c.chiamata_entro}</span>
                  </div>
                  <div data-testid={`chiamata-nome-${c.id}`} className="font-semibold text-sm">{c.nome}</div>
                  {c.via && <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1.5"><MapPin size={12} className="text-[#F43F5E]" /> {c.via}</div>}
                  {c.descrizione && <NoteView text={c.descrizione} className="text-xs text-[var(--muted)] mt-1" />}
                  {c.note && <NoteView text={`Note: ${c.note}`} className="text-xs text-[var(--muted)] mt-1" />}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button data-testid={`call-open-${c.id}`} onClick={() => openCard(c)} className="px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40 flex items-center gap-1.5"><ArrowRight size={13} /> Apri scheda</button>
                    {esitoFor === `${c.tipo}-${c.id}` ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] text-[var(--muted)]">Esito:</span>
                        {[["fatta","Fatta"],["non_risponde","Non risponde"],["da_rifare","Da rifare"]].map(([k,l]) => (
                          <button key={k} data-testid={`call-esito-${k}-${c.id}`} onClick={() => complete(c, k)} className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold">{l}</button>
                        ))}
                      </div>
                    ) : (
                      <button data-testid={`call-complete-${c.id}`} onClick={() => setEsitoFor(`${c.tipo}-${c.id}`)} className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold flex items-center gap-1.5"><Check size={13} /> Completata</button>
                    )}
                    <button data-testid={`call-snooze-${c.id}`} onClick={() => snoozeTomorrow(c)} className="px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40 flex items-center gap-1.5"><CalendarClock size={13} /> Rimanda a domani</button>
                    {rescheduleId === `${c.tipo}-${c.id}` ? (
                      <div className="flex items-center gap-1.5">
                        <input data-testid={`call-newdate-${c.id}`} type="date" value={rdate} onChange={(e) => setRdate(e.target.value)} className="px-2 py-1.5 rounded-lg border border-[var(--border)] text-xs" />
                        <button data-testid={`call-reschedule-confirm-${c.id}`} onClick={() => reschedule(c)} className="px-3 py-1.5 rounded-lg bg-[#FC82ED] text-black text-xs font-semibold">Conferma</button>
                      </div>
                    ) : (
                      <button data-testid={`call-reschedule-${c.id}`} onClick={() => { setRescheduleId(`${c.tipo}-${c.id}`); setRdate(""); }} className="px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40 flex items-center gap-1.5"><CalendarClock size={13} /> Rimanda</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-[var(--border)] shrink-0 bg-[var(--card)]">
              <button onClick={() => setShowModal(false)} className="w-full py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Chiudi</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
